/**
 * Onboarding Manager Component
 *
 * Main component that orchestrates all onboarding functionality.
 */

import React from "react";
import { useLocation } from "react-router-dom";
import { useOnboarding, useTourStep } from "../../contexts/OnboardingContext";
import ErrorBoundary from "../ErrorBoundary";
import { TourEngineHost } from "./engine/TourEngineHost";
import { OnboardingRouteWatcher } from "./OnboardingRouteWatcher";
import { OnboardingInteractionWatcher } from "./OnboardingInteractionWatcher";
import { FirstSessionTourTrigger } from "./FirstSessionTourTrigger";
import { TourNavigationBridge } from "./TourNavigationBridge";
import { LearningLabMissionAutoResume } from "./LearningLabMissionAutoResume";
import { LearningLabMissionProvider } from "../learning-lab/mission/LearningLabMissionProvider";
import { LearningLabMissionPanel } from "../learning-lab/mission/LearningLabMissionPanel";
import { logger } from "../../utils/logger";
import type { TourStep } from "../../types/onboarding";

interface OnboardingManagerProps {
  enabled?: boolean;
}

export const OnboardingManager: React.FC<OnboardingManagerProps> = ({
  enabled = true,
}) => {
  const location = useLocation();
  const { state, config } = useOnboarding();
  const { index: currentIndex, total } = useTourStep();

  if (!config.enabled) {
    return null;
  }

  const isHomePage = location.pathname === "/";

  const handleTourEnd = () => {
    logger.info("Tour ended", {
      flowId: state.currentFlowId,
      stepIndex: currentIndex,
      totalSteps: total,
    });
  };

  const handleStepChange = (step: TourStep, index: number) => {
    logger.debug("Step changed", {
      stepName: step.stepName,
      index,
      flowId: state.currentFlowId,
    });
  };

  if (!enabled || !config.enabled) {
    return null;
  }

  if (isHomePage && !state.isRunning) {
    return null;
  }

  return (
    <>
      <TourNavigationBridge />
      <LearningLabMissionAutoResume />
      <FirstSessionTourTrigger />
      <OnboardingRouteWatcher />
      <OnboardingInteractionWatcher />

      {state.isRunning && state.currentSteps.length > 0 && (
        <ErrorBoundary
          fallback={
            <div role="alert" style={{ display: "none" }}>
              Tour unavailable
            </div>
          }
        >
          <LearningLabMissionProvider>
            <TourEngineHost
              run={state.isRunning}
              steps={state.currentSteps}
              onTourEnd={handleTourEnd}
              onStepChange={handleStepChange}
            />
            <LearningLabMissionPanel />
          </LearningLabMissionProvider>
        </ErrorBoundary>
      )}
    </>
  );
};

export default OnboardingManager;
